import { useMemo } from 'react';

import { BookMarkedIcon, BookUserIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import Brand from '@/components/atoms/Icons/Navigation/Brand';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ListTooltipContent } from '@/components/atoms/ListTooltipContent/ListTooltipContent';
import HGSLogo from '@/components/pages/Home/fieldsets/HomeOverview/HGSPulse';
import { Button } from '@/components/ui/button';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { cn } from '@/lib/utils';
import { modelIconResolver } from '@/modules/developerTools/pages/languageModels/helper';
import { ProviderName } from '@/modules/developerTools/types';
import { selectIframeView } from '@/redux/user/selectors';
import { Collection } from '@/shared/types/collections';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';

const CHANNELS_MAX_DISPLAY = 3;

export const Divider = () => <div className="h-px w-full border-t border-border" />;

export const PersonalizationBox = ({
  label,
  hasWebhook,
  tooltip,
  onClick,
}: {
  label: string;
  hasWebhook: boolean;
  tooltip: string;
  onClick: () => void;
}) => (
  <InfoTooltip content={tooltip} side="top" className="text-center">
    <Button
      variant="ghost"
      className="flex h-32 w-[120px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md p-2 transition-colors hover:bg-highlight-background"
      onClick={onClick}
    >
      <div
        className={cn(
          'h-8 rounded-md border bg-background p-2',
          hasWebhook ? 'border-border' : 'border-dashed border-border'
        )}
      >
        {hasWebhook ? (
          <BookUserIcon className="h-4 w-4 text-foreground-muted" />
        ) : (
          <PlusIcon className="h-4 w-4 text-foreground-muted" />
        )}
      </div>
      <span className="text-center text-xs leading-4 font-medium text-foreground-muted">
        {label}
      </span>
    </Button>
  </InfoTooltip>
);

export const KnowledgeBox = ({
  label,
  collection,
  collections,
  onCollectionChange,
}: {
  label: string;
  collection?: Collection;
  collections: Collection[];
  onCollectionChange: (collectionId: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const hasCollection = !!collection;
  const tooltip = hasCollection
    ? `${t('ai_agents.overview.connected_knowledge')}: ${collection.name}`
    : t('ai_agents.overview.add_knowledge_base');

  const options = useMemo(
    () =>
      collections.map((col) => ({
        value: col.collection_id,
        label: col.name,
        icon: <BookMarkedIcon className="h-4 w-4 text-foreground-muted" />,
        externalLink: `/${slug}/knowledge-bases/${col.collection_id}`,
        externalLinkTooltip: `${t('common.go_to')} ${col.name}`,
      })),
    [collections, slug, t]
  );

  const handleChange = (value: string) => {
    onCollectionChange(value || null);
  };

  // Approximate chars that fit in one line with text-xs in ~104px width
  const isTwoLines = hasCollection && collection.name.length > 14;

  const trigger = (
    <Button
      variant="ghost"
      className={cn(
        'flex h-32 w-[120px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md p-2 transition-colors hover:bg-highlight-background',
        isTwoLines && 'translate-y-2'
      )}
    >
      <div
        className={cn(
          'flex h-8 items-center justify-center rounded-md border bg-background p-2',
          hasCollection ? 'border-border' : 'border-dashed border-border'
        )}
      >
        {hasCollection ? (
          <BookMarkedIcon className="h-4 w-4 text-foreground-muted" />
        ) : (
          <PlusIcon className="h-4 w-4 text-foreground-muted" />
        )}
      </div>
      <div className="w-full min-w-0">
        {hasCollection ? (
          <p className="line-clamp-2 w-full text-center text-xs leading-4 font-medium break-all whitespace-normal text-foreground-muted">
            {collection.name}
          </p>
        ) : (
          <span className="block text-center text-xs leading-4 font-medium text-foreground-muted">
            {label}
          </span>
        )}
      </div>
    </Button>
  );

  return (
    <InfoTooltip
      content={tooltip}
      side="top"
      size="auto"
      className="max-w-[200px] text-center break-all"
    >
      <span>
        <PopoverCombobox
          options={options}
          value={collection?.collection_id || ''}
          onChange={handleChange}
          trigger={trigger}
          allowDeselect
        />
      </span>
    </InfoTooltip>
  );
};

export const AiAgentBox = ({
  label,
  provider,
  tooltip,
  onClick,
}: {
  label: string;
  provider: ProviderName;
  tooltip: string;
  onClick: () => void;
}) => {
  const isIframeView = useSelector(selectIframeView);

  const renderProviderIcon = () => {
    if (provider !== 'moveo') {
      return modelIconResolver(provider, 20);
    }
    if (isIframeView) {
      return <HGSLogo size={20} />;
    }
    return <Brand size={20} />;
  };

  return (
    <InfoTooltip content={tooltip} side="top" className="text-center">
      <Button
        variant="ghost"
        onClick={onClick}
        className="flex h-32 w-[120px] cursor-pointer items-center justify-center rounded-md p-2 transition-colors hover:bg-highlight-background"
      >
        <div className="relative h-24 w-24 rounded-lg border border-border bg-linear-to-b from-background to-bg-muted p-2 shadow-sm">
          <div className="absolute top-1 right-2">{renderProviderIcon()}</div>
          <div className="absolute bottom-2 left-1.5">
            <span className="font-mono text-xs leading-4 text-foreground-muted">{label}</span>
          </div>
          <div className="absolute right-0 bottom-[30px] left-0 h-px bg-border" />
          <div className="absolute top-0 left-[62px] h-16 w-px bg-border" />
        </div>
      </Button>
    </InfoTooltip>
  );
};

export const ChannelsBox = ({
  label,
  channels,
  tooltip,
  onClick,
}: {
  label: string;
  channels: Integration[];
  tooltip: string;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const tooltipItems = channels.map((integration) => {
    const channelLabel = integration.name ?? t(getChannelKey(integration.type));
    const capitalizedLabel = channelLabel.charAt(0).toUpperCase() + channelLabel.slice(1);
    return {
      id: integration.integration_id,
      label: capitalizedLabel,
      href: `/${slug}/environments/${integration.desk_id}/integrations/${integration.integration_id}`,
    };
  });

  const content = (
    <Button
      variant="ghost"
      onClick={onClick}
      className="flex h-32 w-[120px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md p-2 text-foreground transition-colors hover:bg-highlight-background"
    >
      <div className="flex h-8 items-center pr-1 pl-0">
        {channels.length > 0 ? (
          <>
            {channels
              .slice(
                0,
                channels.length > CHANNELS_MAX_DISPLAY
                  ? CHANNELS_MAX_DISPLAY - 1
                  : CHANNELS_MAX_DISPLAY
              )
              .map((integration, index) => {
                const IconComponent = integrationIconByType[integration.type as IntegrationType];
                return (
                  <div
                    key={integration.integration_id}
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-transparent bg-surface-button-secondary',
                      index > 0 && '-ml-1'
                    )}
                  >
                    {IconComponent ? <IconComponent size={16} /> : null}
                  </div>
                );
              })}
            {channels.length > CHANNELS_MAX_DISPLAY && (
              <div className="-ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-transparent bg-surface-button-secondary px-2">
                <span className="text-sm leading-none">+{channels.length - 2}</span>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-md border border-dashed border-border bg-background p-2">
            <PlusIcon className="h-4 w-4 text-foreground-muted" />
          </div>
        )}
      </div>
      <span className="text-center text-xs leading-4 font-medium text-foreground-muted">
        {label}
      </span>
    </Button>
  );

  if (channels.length === 0) {
    return (
      <InfoTooltip content={tooltip} side="top" className="text-center">
        {content}
      </InfoTooltip>
    );
  }

  return (
    <InfoTooltip
      variant="rich"
      size="auto"
      side="bottom"
      align="center"
      content={
        <ListTooltipContent
          title={t('ai_agents.overview.connected_channels')}
          items={tooltipItems}
        />
      }
    >
      {content}
    </InfoTooltip>
  );
};

export const ConversationsBox = ({ label, count }: { label: string; count: number }) => (
  <div className="flex h-32 w-[120px] translate-y-2 flex-col items-center justify-center gap-3 rounded p-2">
    <div className="flex h-8 min-w-8 items-center justify-center rounded-md border border-border bg-background p-2">
      <span className="text-center text-base leading-6 font-semibold">{count}</span>
    </div>
    <span className="w-full text-center text-xs leading-4 font-medium text-foreground-muted">
      {label}
    </span>
  </div>
);
